import React, { FunctionComponent, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import WheelAnalyticsManager from '../../analytics/WheelAnalyticsManager'
import {
  async_fetch_ml_config,
  nextLotteryStep,
} from '../../redux/actions/lottery'
import WheelButton from '../../components/cta/WheelButton'
import LotteryV2StepType from '@tootsweet/model/lottery/LotteryV2StepType'
import moment from 'moment'
import { tt } from '../../i18n'
import TSConstants from '../../utils/TSConstants'
import TitleV2 from '../../components/MonthlyLottery/Title/TitleV2'
import { QUIZZ_STEP_ML_GAME } from '../../redux/reducers/quizz'
import { setQuizzStep } from '../../redux/actions/quizz'
import { mlConfigSelector } from '../../redux/selectors/lottery'
import MonthlyLotteryCarousel from './MonthlyLotteryCarousel'
import './styles.scss'

interface Props {
  isQuizz?: boolean
  skillNextStep?: () => void
}

const MonthlyLotteryIntro: FunctionComponent<Props> = (props) => {
  const dispatch = useDispatch()

  const mlConfig = useSelector(mlConfigSelector)

  useEffect(() => {
    WheelAnalyticsManager.logEvent(LotteryV2StepType.ML_Intro)
    dispatch(async_fetch_ml_config())
  }, [dispatch])

  const next = () => {
    if (props.skillNextStep) {
      props.skillNextStep()
    }
    if (props.isQuizz) {
      dispatch(setQuizzStep(QUIZZ_STEP_ML_GAME))
    }
    dispatch(nextLotteryStep())
  }

  const prizes = useMemo(() => {
    if (mlConfig?.prizes && mlConfig.prizes.length > 0) {
      return mlConfig.prizes
    }
    if (mlConfig?.img) {
      return [{ img: mlConfig.img, prize: mlConfig.prize || '' }]
    }
    return []
  }, [mlConfig?.prizes, mlConfig?.img, mlConfig?.prize])

  const remainingDaysText = useMemo(
    () => renderRemainingDays(mlConfig?.drawTs),
    [mlConfig?.drawTs]
  )

  if (!mlConfig) {
    return null
  }

  return (
    <>
      <div className="cm-pad w-margin-top w-margin-bottom-half">
        <TitleV2 id="k-ml-title" title1={mlConfig?.title} />
      </div>

      <div className="cm-pad k-glm-text">
        <p>{mlConfig?.text}</p>
      </div>

      <MonthlyLotteryCarousel
        prizes={prizes}
        drawTs={mlConfig.drawTs}
        remainingDaysText={remainingDaysText}
      />

      <WheelButton
        id="k-glm-intro-cta"
        text={tt('start_the_game')}
        //containerClassName="k-glm-intro-bottom-btn"
        onClick={next}
        isFixedBottom={true}
      />
    </>
  )
}

export default MonthlyLotteryIntro

function renderRemainingDays(drawTs: number | undefined) {
  const rem = computeRemainingDays(drawTs)
  if (rem.days < 1) {
    let hours = rem.hours
    if (hours < 1) {
      return tt('ends_at_midnight')
    }
    if (hours <= 1) {
      return `${hours} ${tt('remaining_hours_singular')}`
    }
    return `${hours} ${tt('remaining_hours_plural')}`
  }
  return (
    <>
      {rem.days} {rem.days > 1 ? tt('days') : tt('day')}{' '}
      {rem.days > 1
        ? tt('remaining_days_plural')
        : tt('remaining_days_singular')}
    </>
  )
}

function computeRemainingDays(drawTs: number | undefined) {
  const endOfGameDate = drawTs
    ? moment(drawTs, 'X')
    : moment().add(1, 'month').startOf('month')
  const today = moment()
  const diffTs = endOfGameDate.unix() - today.unix()
  const days = diffTs / TSConstants.secondsInADay
  const hours =
    (diffTs % TSConstants.secondsInADay) / TSConstants.secondsInAnHour
  return {
    days: Math.trunc(days),
    hours: Math.trunc(hours),
  }
}
